import { useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { z } from 'zod'

import { publicApi } from '@/api/public'
import { Alert, AuthLayout, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'

const MIN_PASSWORD_LENGTH = 10

const schema = z.object({
  organizationName: z.string().min(2),
  fullName: z.string().min(2),
  phone: z.string().max(32).optional().or(z.literal('')),
  vatNumber: z.string().max(20).optional().or(z.literal('')),
  password: z.string().min(MIN_PASSWORD_LENGTH),
})

type FormValues = z.infer<typeof schema>

/**
 * Landing εγγραφής προμηθευτή (§7.4, §11).
 * Το κίνητρο είναι το πλήθος πελατών που ήδη του στέλνουν παραγγελίες.
 */
export default function PublicInvitePage() {
  const { t } = useTranslation()
  const { token = '' } = useParams()
  const translateError = useApiError()
  const [sentTo, setSentTo] = useState<string | null>(null)

  const invite = useQuery({
    queryKey: ['public-invite', token],
    queryFn: () => publicApi.invite(token),
    retry: false,
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const claim = useMutation({
    mutationFn: (values: FormValues) =>
      publicApi.claim(token, {
        ...values,
        phone: values.phone || undefined,
        vatNumber: values.vatNumber || undefined,
      }),
    onSuccess: (result) => setSentTo(result.email),
  })

  const data = invite.data

  if (sentTo) {
    return (
      <AuthLayout title={t('invite.verifyTitle')}>
        <Alert tone="success">{t('invite.verifySent', { email: sentTo })}</Alert>
        <p className="mt-3 text-sm text-gray-500">{t('invite.verifyHint')}</p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t('invite.title')} subtitle={data?.supplierName}>
      {invite.isPending && <p className="text-gray-500">{t('common.loading')}</p>}
      {invite.isError && <Alert>{translateError(invite.error)}</Alert>}

      {data?.alreadyClaimed && (
        <div className="flex flex-col gap-4">
          <Alert tone="success">{t('invite.alreadyClaimed')}</Alert>
          <Link
            to="/login"
            className="btn inline-flex w-full items-center justify-center rounded-xl bg-brand-700 px-5 text-base font-semibold text-white hover:bg-brand-600"
          >
            {t('auth.login.submit')}
          </Link>
        </div>
      )}

      {data && !data.alreadyClaimed && (
        <div className="flex flex-col gap-5">
          <div className="rounded-xl border border-brand-200 bg-brand-50 p-4 text-center">
            <p className="text-4xl font-bold tabular-nums text-brand-700">{data.customerCount}</p>
            <p className="mt-1 text-sm text-brand-900">
              {t('invite.customerCount', { count: data.customerCount })}
            </p>
            <p className="mt-2 text-xs text-brand-800">
              {t('invite.orderCount', { count: data.orderCount })}
            </p>
          </div>

          <p className="text-sm leading-relaxed text-gray-600">{t('invite.pitch')}</p>

          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit((values) => claim.mutate(values))}
            noValidate
          >
            {claim.isError && <Alert>{translateError(claim.error)}</Alert>}

            {/* Το email δεν είναι επεξεργάσιμο: είναι η ταυτότητα του προμηθευτή. */}
            <Field
              label={t('auth.fields.email')}
              name="email"
              value={data.email}
              readOnly
              disabled
              hint={t('invite.emailFixed')}
            />

            <Field
              label={t('auth.fields.organizationName')}
              autoFocus
              error={errors.organizationName && t('auth.validation.organizationName')}
              {...register('organizationName')}
            />
            <Field
              label={t('auth.fields.vatNumber')}
              inputMode="numeric"
              {...register('vatNumber')}
            />
            <Field
              label={t('auth.fields.fullName')}
              autoComplete="name"
              error={errors.fullName && t('auth.validation.fullName')}
              {...register('fullName')}
            />
            <Field
              label={t('auth.fields.phone')}
              type="tel"
              inputMode="tel"
              {...register('phone')}
            />
            <Field
              label={t('auth.fields.password')}
              type="password"
              autoComplete="new-password"
              hint={t('auth.validation.passwordHint', { count: MIN_PASSWORD_LENGTH })}
              error={errors.password && t('auth.validation.passwordHint', { count: MIN_PASSWORD_LENGTH })}
              {...register('password')}
            />

            <Button type="submit" loading={claim.isPending}>
              {t('invite.submit')}
            </Button>
          </form>
        </div>
      )}
    </AuthLayout>
  )
}
